import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ScanLine, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface Banner {
  seq_banner: number;
  cod_banner: string;
  nom_banner: string;
  des_titulo: string | null;
  des_subtitulo: string | null;
  des_imagem_url: string;
  des_link_destino: string | null;
  cod_cta: string | null;
  num_ordem: number;
  qtd_segundos_duracao: number;
}

interface HeroProps {
  /** Texto curto pra saudação no overlay (ex: "Bom dia, Joao 👋"). */
  saudacao?: string;
  /** Renderizado quando NAO ha banner aplicavel (fallback). */
  fallback?: React.ReactNode;
}

/**
 * BannerCarousel — Hero comercial da home do cliente.
 *
 * Quando ha banners aplicaveis: mostra carrossel auto-rotativo grande,
 * com logo do tenant marca-d'agua, CTA brand-tinted (shadow protagonista) + shimmer.
 * Saudação do cliente vai overlay sobre o banner.
 *
 * Quando NAO ha banners: renderiza `fallback` (hero compacto alternativo).
 *
 * Filtros server-side via fn_banners_visiveis (dia/hora/audiencia/datas).
 * Tracking VIEW (1x/dia) e CLICK via fn_registrar_evento_banner.
 * Realtime: novo banner ativado pelo admin aparece sem refresh.
 */
export function BannerCarousel({ saudacao, fallback }: HeroProps = {}) {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);
  const touchInicioX = useRef<number | null>(null);
  const viewRegistradas = useRef<Set<number>>(new Set());

  const banners = useQuery({
    queryKey: ['banners-visiveis', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .maybeSingle();
      const { data, error } = await sb.rpc('fn_banners_visiveis', {
        p_seq_cliente: cli?.seq_cliente ?? null,
      });
      if (error) throw error;
      return (data ?? []) as Banner[];
    },
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000,
  });

  useRealtimeInvalidate('tab_banners_promocionais', ['banners-visiveis']);

  const lista = useMemo(() => banners.data ?? [], [banners.data]);

  useEffect(() => {
    if (indice >= lista.length && lista.length > 0) setIndice(0);
  }, [lista.length, indice]);

  useEffect(() => {
    if (lista.length <= 1 || pausado) return;
    const atual = lista[indice];
    if (!atual) return;
    const ms = (atual.qtd_segundos_duracao ?? 5) * 1000;
    const timer = setTimeout(() => {
      setIndice((i) => (i + 1) % lista.length);
    }, ms);
    return () => clearTimeout(timer);
  }, [indice, lista, pausado]);

  useEffect(() => {
    const atual = lista[indice];
    if (!atual) return;
    if (viewRegistradas.current.has(atual.seq_banner)) return;
    viewRegistradas.current.add(atual.seq_banner);
    void sb.rpc('fn_registrar_evento_banner', {
      p_seq_banner: atual.seq_banner,
      p_cod_evento: 'VIEW',
      p_user_agent: navigator.userAgent.slice(0, 200),
    });
  }, [indice, lista]);

  const proximo = useCallback(() => setIndice((i) => (i + 1) % lista.length), [lista.length]);
  const anterior = useCallback(
    () => setIndice((i) => (i - 1 + lista.length) % lista.length),
    [lista.length],
  );

  function onTouchStart(e: React.TouchEvent) {
    touchInicioX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchInicioX.current == null) return;
    const fim = e.changedTouches[0]?.clientX ?? touchInicioX.current;
    const delta = fim - touchInicioX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) proximo();
      else anterior();
    }
    touchInicioX.current = null;
  }

  function clicarBanner(b: Banner) {
    void sb.rpc('fn_registrar_evento_banner', {
      p_seq_banner: b.seq_banner,
      p_cod_evento: 'CLICK',
      p_user_agent: navigator.userAgent.slice(0, 200),
    });
    if (!b.des_link_destino) return;
    if (b.des_link_destino.startsWith('http')) {
      window.open(b.des_link_destino, '_blank', 'noopener,noreferrer');
    } else {
      navigate(b.des_link_destino);
    }
  }

  if (banners.isLoading) {
    return <div className="bg-muted h-56 animate-pulse rounded-2xl sm:h-72" />;
  }
  if (lista.length === 0) {
    return fallback ? <>{fallback}</> : null;
  }

  const atual = lista[indice];
  if (!atual) return null;

  return (
    <div
      key={atual.seq_banner}
      className="shadow-octa-lg octa-shimmer relative overflow-hidden rounded-2xl"
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-label="Banners promocionais"
      aria-roledescription="carousel"
    >
      {/* Imagem de fundo */}
      <div className="relative h-60 w-full sm:h-72">
        <img
          src={atual.des_imagem_url}
          alt={atual.des_titulo ?? atual.nom_banner}
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        {/* Overlay gradient pra legibilidade do texto branco */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        {/* Overlay extra com tinta da marca pra fundir com identidade (sem amber) */}
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-overlay"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--primary) / 0.18) 0%, transparent 50%, hsl(210 95% 38% / 0.12) 100%)',
          }}
        />

        {/* Logo do tenant marca-d'agua (canto superior esquerdo) */}
        {tenant?.des_logo_url ? (
          <div className="glass octa-fade-in-up absolute left-3 top-3 flex items-center gap-2 rounded-full px-3 py-1.5 shadow-lg">
            <img
              src={tenant.des_logo_url}
              alt={tenant.nom_tenant}
              className="h-5 w-5 rounded-full object-cover"
            />
            <span className="text-foreground text-xs font-semibold">{tenant.nom_tenant}</span>
          </div>
        ) : (
          <div className="glass octa-fade-in-up absolute left-3 top-3 flex items-center gap-2 rounded-full px-3 py-1.5 shadow-lg">
            <div className="bg-octa-gradient flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white">
              {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
            </div>
            <span className="text-foreground text-xs font-semibold">
              {tenant?.nom_tenant ?? 'Octa Pontos'}
            </span>
          </div>
        )}

        {/* Saudação curta no canto superior direito */}
        {saudacao && (
          <div className="octa-fade-in-up absolute right-3 top-3 rounded-full bg-black/40 px-3 py-1.5 backdrop-blur-md">
            <span className="text-xs font-medium text-white">{saudacao}</span>
          </div>
        )}

        {/* Decoracao flutuante — branco translúcido (sem amber) */}
        <Sparkles
          aria-hidden
          className="octa-float absolute right-6 top-16 h-6 w-6 text-white/50"
        />

        {/* Conteudo do banner — bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          {atual.des_titulo && (
            <h2
              className="octa-fade-in-up text-xl font-bold leading-tight text-white drop-shadow-lg sm:text-2xl"
              style={{ animationDelay: '0.1s' }}
            >
              {atual.des_titulo}
            </h2>
          )}
          {atual.des_subtitulo && (
            <p
              className="octa-fade-in-up mt-1 text-sm text-white/90 drop-shadow-md sm:text-base"
              style={{ animationDelay: '0.2s' }}
            >
              {atual.des_subtitulo}
            </p>
          )}

          <div className="octa-fade-in-up mt-4" style={{ animationDelay: '0.3s' }}>
            {atual.cod_cta ? (
              <button
                type="button"
                onClick={() => clicarBanner(atual)}
                className="bg-octa-gradient shadow-brand-cta-hero hover-cta-lift group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white"
              >
                {atual.cod_cta}
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </button>
            ) : (
              // Sem CTA próprio: mostra "Gerar código agora" brand-tinted protagonista
              <Link
                to="/gerar-codigo"
                onClick={() =>
                  void sb.rpc('fn_registrar_evento_banner', {
                    p_seq_banner: atual.seq_banner,
                    p_cod_evento: 'CLICK',
                    p_user_agent: navigator.userAgent.slice(0, 200),
                  })
                }
                className="bg-octa-gradient shadow-brand-cta-hero hover-cta-lift group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white"
              >
                <ScanLine className="h-4 w-4" />
                Gerar codigo agora
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Setas (so se houver mais de 1) */}
      {lista.length > 1 && (
        <>
          <button
            type="button"
            onClick={anterior}
            aria-label="Banner anterior"
            className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur transition-colors hover:bg-black/60 sm:block"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={proximo}
            aria-label="Próximo banner"
            className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur transition-colors hover:bg-black/60 sm:block"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Dots de navegacao */}
      {lista.length > 1 && (
        <div
          className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5"
          role="tablist"
          aria-label="Slides do carrossel"
        >
          {lista.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndice(i)}
              aria-label={`Ir para slide ${i + 1}`}
              aria-selected={i === indice}
              role="tab"
              className={`h-1.5 rounded-full transition-all ${
                i === indice ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * HeroFallback — usado quando não ha banners ativos.
 * Mantem identidade comercial: gradient brand + CTA brand-tinted protagonista.
 * Compacto pra não competir com KPIs/ações embaixo.
 */
export function HeroFallback({ saudacao, subtitulo }: { saudacao: string; subtitulo: string }) {
  const { tenant } = useTenant();
  return (
    <div className="bg-octa-gradient shadow-octa-lg relative overflow-hidden rounded-2xl">
      {/* Decoracao radial */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/10 blur-2xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-16 -left-12 h-40 w-40 rounded-full bg-white/5 blur-2xl"
      />

      <div className="relative z-10 p-6">
        {/* Logo do tenant (compacto) */}
        <div className="octa-fade-in-up flex items-center gap-2">
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={tenant.nom_tenant}
              className="h-6 w-6 rounded-full bg-white object-cover ring-2 ring-white/30"
            />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-[10px] font-bold text-white ring-2 ring-white/30">
              {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
            </div>
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-white/90">
            {tenant?.nom_tenant ?? 'Octa Pontos'}
          </span>
        </div>

        <h2
          className="octa-fade-in-up mt-3 text-2xl font-bold leading-tight text-white drop-shadow-md"
          style={{ animationDelay: '0.1s' }}
        >
          {saudacao}
        </h2>
        <p
          className="octa-fade-in-up mt-1 text-sm text-white/85"
          style={{ animationDelay: '0.2s' }}
        >
          {subtitulo}
        </p>

        <div className="octa-fade-in-up mt-5" style={{ animationDelay: '0.3s' }}>
          <Button
            asChild
            className="bg-octa-gradient shadow-brand-cta-hero hover-cta-lift h-11 rounded-full px-6 text-base font-bold text-white"
          >
            <Link to="/gerar-codigo">
              <ScanLine className="h-5 w-5" />
              Gerar codigo agora
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
